import React, { useState, useMemo } from 'react';
import {
    Header,
    Container,
    Group, Text, useMantineColorScheme,
    UnstyledButton,
    Menu,
    Divider,
    Image,
    Avatar,
    Box,
    Burger,
    Drawer,
    Stack,
    Badge
} from '@mantine/core';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import {
    IconSun,
    IconMoonStars,
    IconLogout,
    IconSwitchHorizontal
} from '@tabler/icons-react';
import { useLogout } from '../../hooks/useLogout';
import { NavbarGroupSelector } from '../dashboard/GroupSelector';
import { NavbarComponentProjectSelector } from '../admin/NavbarComponentProjectSelector';
import { useViewModeStore } from '../../stores/viewModeStore';
import { useAdminSubawardeeFilterStore } from '../../stores/adminSubawardeeFilterStore';
import { useAdminMilestoneViewStore } from '../../stores/adminMilestoneViewStore';
import { withAdminSubawardeeQuery, buildAdminMilestonesPath } from '../../utils/adminSubawardeePaths';
import { APP_VERSION } from '../../appVersion';

interface NavLinkProps {
  label: string;
  path: string;
  isActive: boolean;
  onClick: () => void;
}

interface NavItem {
  label: string;
  path: string;
  isExternal?: boolean;
}

function NavLink({ label, path, isActive, onClick }: NavLinkProps) {
  return (
    <UnstyledButton
      onClick={onClick}
      sx={(theme) => ({
        display: 'block',
        padding: '8px 12px',
        borderRadius: theme.radius.sm,
        color: isActive 
          ? theme.colorScheme === 'dark' ? theme.white : theme.black
          : theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[7],
        backgroundColor: isActive 
          ? theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1]
          : 'transparent',
        fontWeight: 500,
        '&:hover': {
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        }
      })}
    >
      {label}
    </UnstyledButton>
  );
}

export function Navbar() {
  const [mobileOpened, setMobileOpened] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, hasRole } = useAuthStore();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';
  const { handleLogout } = useLogout();
  const { viewMode, toggleViewMode } = useViewModeStore();
  const adminSubawardee = useAdminSubawardeeFilterStore((s) => s.subawardee);
  const milestoneView = useAdminMilestoneViewStore((s) => s.view);
  
  // Simple admin check - same pattern as GroupSelector
  const canSwitchViewMode = hasRole('admin');

  const handleViewModeToggle = () => {
    const newMode = viewMode === 'standard' ? 'admin' : 'standard';
    toggleViewMode();
    if (newMode === 'admin') {
      navigate('/admin/dashboard');
    } else {
      navigate('/');
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const inAdminSection = location.pathname.startsWith('/admin');
  const showAdminNav =
    canSwitchViewMode && (viewMode === 'admin' || inAdminSection);

  const brandHomePath =
    !isAuthenticated
      ? '/login'
      : showAdminNav
        ? '/admin/dashboard'
        : '/';

  const navItems: NavItem[] = useMemo(() => {
    if (showAdminNav) {
      return [
        { label: 'Dashboard', path: '/admin/dashboard' },
        { label: 'Reports', path: withAdminSubawardeeQuery('/admin/reports', adminSubawardee) },
        { label: 'Milestones', path: buildAdminMilestonesPath({
          view: milestoneView,
          subawardee: adminSubawardee,
        }) },
      ];
    }
    return [
      { label: 'Dashboard', path: '/' },
      { label: 'Reports', path: '/reports' },
      { label: 'Milestones', path: '/milestones' },
    ];
  }, [showAdminNav, adminSubawardee, milestoneView]);

  const isNavItemActive = (path: string) => {
    const pathBase = path.split('?')[0];
    if (pathBase === '/admin/dashboard') {
      return location.pathname === '/admin' || location.pathname.startsWith('/admin/dashboard');
    }
    if (pathBase === '/admin/milestones') {
      return location.pathname.startsWith('/admin/milestones');
    }
    if (pathBase === '/admin/reports') {
      return location.pathname.startsWith('/admin/reports');
    }
    return location.pathname === pathBase;
  };

  const navLinks = navItems.map((item) => (
    item.isExternal ? (
      <UnstyledButton
        key={item.label}
        component="a"
        href={item.path}
        target="_blank"
        rel="noopener noreferrer"
        sx={(theme) => ({
          display: 'block',
          padding: '8px 12px',
          borderRadius: theme.radius.sm,
          color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[7],
          backgroundColor: 'transparent',
          fontWeight: 500,
          '&:hover': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
          }
        })}
      >
        {item.label}
      </UnstyledButton>
    ) : (
      <NavLink
        key={item.label}
        label={item.label}
        path={item.path}
        isActive={isNavItemActive(item.path)}
        onClick={() => {
          handleNavigation(item.path);
          setMobileOpened(false);
        }}
      />
    )
  ));

  return (
    <Header height={70} mb="xl">
      <Container fluid px={0}>
        <Group position="apart" h={70} px="md" noWrap sx={(theme) => ({
          '@media (max-width: 768px)': {
            paddingLeft: theme.spacing.xs,
            paddingRight: theme.spacing.xs,
            height: 70,
            alignItems: 'center',
            minHeight: 70
          }
        })}>
          <UnstyledButton 
            onClick={() => handleNavigation(brandHomePath)} 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              overflow: 'hidden',
              flex: 1,
              minWidth: 0,
              height: '100%',
              '@media (max-width: 768px)': {
                flex: 1,
                minWidth: 0,
                maxWidth: 'calc(100vw - 60px)',
                height: 70
              }
            }}
          >
            <Group spacing="sm" noWrap sx={{ 
              minWidth: 0, 
              flex: 1,
              height: '100%',
              alignItems: 'center',
              '@media (max-width: 768px)': {
                spacing: 6,
                flex: 1,
                alignItems: 'center',
                height: 70
              }
            }}>
              <Image
                src={dark ? "/EDA_Tech_Hubs_Logo_White_Square.png" : "/EDA_Tech_Hubs_Logo_Blue_Square.png"}
                alt="EDA Tech Hubs Logo"
                width={80}
                height={50}
                fit="contain"
                sx={(theme) => ({
                  flexShrink: 0,
                  '@media (max-width: 768px)': {
                    width: 24,
                    height: 24,
                    objectFit: 'contain',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }
                })}
              />
              <Text 
                size="lg" 
                weight={700} 
                sx={(theme) => ({
                  minWidth: 'max-content',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  '@media (max-width: 1200px)': {
                    fontSize: theme.fontSizes.md
                  },
                  '@media (max-width: 768px)': {
                    display: 'none'
                  }
                })}
              >
                Performance Management Portal
              </Text>
              {canSwitchViewMode && (viewMode === 'admin' || inAdminSection) && (
                                  <Badge variant="light" color="blue" size="sm">
                    Admin View
                </Badge>
              )}
            </Group>
          </UnstyledButton>
          
          {isAuthenticated ? (
            <Group spacing="md" noWrap>
              {/* Desktop Navigation */}
              <Group spacing="sm" sx={{ '@media (max-width: 1024px)': { display: 'none' } }}>
                {navLinks}
              </Group>

              {/* Mobile Menu Button */}
              <Burger
                opened={mobileOpened}
                onClick={() => setMobileOpened((o) => !o)}
                sx={{ '@media (min-width: 1025px)': { display: 'none' } }}
              />

              {/* Subawardee group selector (standard view) or component project scope (admin view) */}
              {viewMode !== 'admin' && !inAdminSection ? (
                <Box
                  sx={(theme) => ({
                    borderLeft: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
                    paddingLeft: theme.spacing.md,
                    display: 'flex',
                    alignItems: 'center',
                    '@media (max-width: 1024px)': { display: 'none' }
                  })}
                >
                  <NavbarGroupSelector />
                </Box>
              ) : null}
              {showAdminNav ? (
                <Box
                  sx={(theme) => ({
                    borderLeft: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
                    paddingLeft: theme.spacing.md,
                    display: 'flex',
                    alignItems: 'center',
                    '@media (max-width: 1024px)': { display: 'none' }
                  })}
                >
                  <NavbarComponentProjectSelector />
                </Box>
              ) : null}

              {/* User Menu - Desktop Only */}
              <Box sx={{ '@media (max-width: 1024px)': { display: 'none' } }}>
                <Menu shadow="md" width={200} position="bottom-end">
                  <Menu.Target>
                    <UnstyledButton
                      sx={(theme) => ({
                        borderRadius: theme.radius.sm,
                        color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[7],
                        '&:hover': {
                          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
                        },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        width: '40px',
                        height: '40px',
                        position: 'relative'
                      })}
                    >
                      <Avatar 
                        radius="xl" 
                        size={32}
                        color="blue"
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)'
                        }}
                      >
                        {user?.name?.charAt(0).toUpperCase() || 'U'}
                      </Avatar>
                    </UnstyledButton>
                  </Menu.Target>

                  <Menu.Dropdown>
                    <Menu.Label>Signed in as</Menu.Label>
                    <Text size="sm" px="md" pb="xs" weight={500}>
                      {user?.name || 'User'}
                    </Text>

                    <Divider my="xs" />

                    <Menu.Label>Theme</Menu.Label>
                    <Menu.Item
                      icon={dark ? <IconSun size={16} /> : <IconMoonStars size={16} />}
                      onClick={() => toggleColorScheme()}
                    >
                      {dark ? 'Light Mode' : 'Dark Mode'}
                    </Menu.Item>

                    {canSwitchViewMode && (
                      <>
                        <Divider my="xs" />
                        <Menu.Label>View Mode</Menu.Label>
                        <Menu.Item
                          icon={<IconSwitchHorizontal size={16} />}
                          onClick={handleViewModeToggle}
                        >
                          {viewMode === 'standard' ? 'Admin View' : 'Standard View'}
                        </Menu.Item>
                      </>
                    )}

                    <Divider my="xs" />

                    <Menu.Label>Account</Menu.Label>
                    <Menu.Item
                      color="red"
                      icon={<IconLogout size={16} />}
                      onClick={handleLogout}
                    >
                      Logout
                    </Menu.Item>

                    <Divider my="xs" />

                    <Menu.Label>App Info</Menu.Label>
                    <Text size="sm" color="dimmed" px="md" pb="xs">
                      Version {APP_VERSION}
                    </Text>
                  </Menu.Dropdown>
                </Menu>
              </Box>
            </Group>
          ) : (
            <UnstyledButton
              onClick={() => toggleColorScheme()}
              sx={(theme) => ({
                backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
                color: theme.colorScheme === 'dark' ? theme.colors.yellow[4] : theme.colors.blue[6],
                padding: theme.spacing.xs,
                borderRadius: theme.radius.sm,
                '&:hover': {
                  backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1],
                },
              })}
            >
              {dark ? <IconSun size={18} /> : <IconMoonStars size={18} />}
            </UnstyledButton>
          )}
        </Group>
      </Container>

      {/* Mobile Navigation Drawer */}
      <Drawer
        opened={mobileOpened}
        onClose={() => setMobileOpened(false)}
        size="100%"
        padding="md"
        title={
          <Group>
            <Avatar 
              radius="xl" 
              size={32}
              color="blue"
            >
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
            <Text weight={500}>{user?.name || 'User'}</Text>
          </Group>
        }
        sx={{ '@media (min-width: 1025px)': { display: 'none' } }}
      >
        <Stack spacing="md">
          {navLinks}
          <Divider my="xs" />
          {viewMode !== 'admin' && !inAdminSection ? (
            <>
              <NavbarGroupSelector />
              <Divider my="xs" />
            </>
          ) : null}
          {showAdminNav ? (
            <>
              <NavbarComponentProjectSelector />
              <Divider my="xs" />
            </>
          ) : null}
          <UnstyledButton
            onClick={() => toggleColorScheme()}
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              borderRadius: theme.radius.sm,
              color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[7],
              '&:hover': {
                backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
              },
            })}
          >
            {dark ? <IconSun size={16} /> : <IconMoonStars size={16} />}
            <Text>{dark ? 'Light Mode' : 'Dark Mode'}</Text>
          </UnstyledButton>
          {canSwitchViewMode && (
            <UnstyledButton
              onClick={handleViewModeToggle}
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                borderRadius: theme.radius.sm,
                color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[7],
                '&:hover': {
                  backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
                },
              })}
            >
              <IconSwitchHorizontal size={16} />
              <Text>{viewMode === 'standard' ? 'Admin View' : 'Standard View'}</Text>
            </UnstyledButton>
          )}
          <UnstyledButton
            onClick={handleLogout}
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              borderRadius: theme.radius.sm,
              color: theme.colors.red[6],
              '&:hover': {
                backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
              },
            })}
          >
            <IconLogout size={16} />
            <Text>Logout</Text>
          </UnstyledButton>
        </Stack>
      </Drawer>
    </Header>
  );
} 